/* ──────────────────────────────────────────────────────────────────────
 * PipelineDonut — 180×180 segmented donut
 *
 * Raw inline SVG (no chart lib). 6 stage segments separated by 4px
 * white gaps. Each segment animates its stroke-dasharray on mount
 * with a 150ms stagger. Hover any segment → it scales up 1.05x, the
 * others dim to 40%, and the centre text swaps to that segment's
 * stage + count.
 *
 * Wired to take its 6 stage values directly — caller decides whether
 * those come from the per-user pipeline or the team-wide one.
 * ──────────────────────────────────────────────────────────────────── */

import React, { useState } from "react";
import { Box, Typography, Stack } from "@mui/material";

const INK         = "#0F172A";
const SLATE       = "#64748b";
const HAIRLINE    = "rgba(10,14,26,0.06)";
const FONT_SERIF  = `'Fraunces', 'GT Sectra', Georgia, serif`;
const FONT_MONO   = `'JetBrains Mono', 'SF Mono', Menlo, monospace`;

export interface DonutSegment {
  key: string;
  label: string;
  count: number;
  color: string;
}

interface Props { segments: DonutSegment[]; }

const VBOX   = 180;
const CENTER = VBOX / 2;
const R      = 70;          // donut middle-radius
const W      = 22;          // ring thickness
const CIRC   = 2 * Math.PI * R;
const GAP_DEG = 3;          // gap between segments in degrees

export default function PipelineDonut({ segments }: Props) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // Filter zero segments out of the donut itself but keep them in the
  // legend so the rep sees every stage. Total drives proportions.
  const visibleSegments = segments.filter((s) => s.count > 0);
  const total = visibleSegments.reduce((s, x) => s + x.count, 0);
  const grandTotal = segments.reduce((s, x) => s + x.count, 0);

  // Convert each segment to start-angle / sweep-angle pairs. Segments
  // accumulate clockwise from 12 o'clock (-90°).
  let cursor = -90;
  const arcs = visibleSegments.map((s) => {
    const sweep = (s.count / Math.max(1, total)) * 360 - GAP_DEG;
    const a = { ...s, start: cursor, sweep: Math.max(0.1, sweep) };
    cursor += sweep + GAP_DEG;
    return a;
  });

  const empty = total === 0;
  const hoverSegment = hoverIdx !== null ? visibleSegments[hoverIdx] : null;

  return (
    <Stack direction="row" alignItems="center" gap={3} flexWrap="wrap">
      {/* ── Donut SVG ────────────────────────────────────────────── */}
      <Box sx={{ position: "relative", width: VBOX, height: VBOX, flexShrink: 0 }}>
        <Box
          component="svg"
          viewBox={`0 0 ${VBOX} ${VBOX}`}
          sx={{
            width: "100%", height: "100%", display: "block",
            "& .donut-seg": {
              transition: "opacity 250ms cubic-bezier(0.16, 1, 0.3, 1), transform 250ms cubic-bezier(0.16, 1, 0.3, 1)",
              transformOrigin: `${CENTER}px ${CENTER}px`,
              cursor: "pointer",
            },
            "&:hover .donut-seg": { opacity: 0.4 },
            "& .donut-seg:hover": {
              opacity: 1,
              transform: "scale(1.05)",
            },
          }}
        >
          {/* Faint track ring — visible only when the donut is empty
              or when segments don't sum to 360°. */}
          <circle
            cx={CENTER} cy={CENTER} r={R}
            fill="none"
            stroke="rgba(10,14,26,0.05)"
            strokeWidth={W}
          />

          {empty ? (
            // Empty-state: a faint dashed ring + a soft slate caption.
            <circle
              cx={CENTER} cy={CENTER} r={R}
              fill="none"
              stroke="rgba(10,14,26,0.08)"
              strokeWidth={W}
              strokeDasharray="4 6"
            />
          ) : (
            arcs.map((a, i) => {
              const sweepPct = a.sweep / 360;
              const dashLen  = CIRC * sweepPct;
              // Offset rotates the dash start to the segment's start angle.
              // stroke-dashoffset of -((start+90)/360)*CIRC aligns the dash
              // start with the desired start angle on a circle that begins
              // at 3-o'clock when un-rotated. We also rotate the circle so
              // 12-o'clock = 0° start — that gives us a cleaner math.
              return (
                <circle
                  key={a.key}
                  className="donut-seg"
                  cx={CENTER} cy={CENTER} r={R}
                  fill="none"
                  stroke={a.color}
                  strokeWidth={W}
                  strokeLinecap="butt"
                  pathLength={CIRC}
                  strokeDasharray={`${dashLen} ${CIRC - dashLen}`}
                  strokeDashoffset={CIRC - ((a.start + 90) / 360) * CIRC + CIRC}
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx(null)}
                  style={{
                    // Per-segment fade-in stagger on mount. The
                    // dasharray itself draws via CSS opacity here
                    // since stagger-via-dashoffset would clash with
                    // the rotational offset above.
                    opacity: 0,
                    animation: `donut-fade-${i} 600ms ${i * 150}ms cubic-bezier(0.16, 1, 0.3, 1) forwards`,
                    [`@keyframes donut-fade-${i}` as any]: {
                      from: { opacity: 0, strokeWidth: W * 0.6 },
                      to:   { opacity: 1, strokeWidth: W },
                    },
                  }}
                />
              );
            })
          )}
        </Box>

        {/* Centre label — total leads / hovered segment count */}
        <Box sx={{
          position: "absolute",
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          pointerEvents: "none",
        }}>
          <Typography sx={{
            fontFamily: FONT_SERIF,
            fontSize: 38, fontWeight: 300,
            color: INK, lineHeight: 0.95,
            letterSpacing: "-0.025em",
            fontVariantNumeric: "tabular-nums",
          }}>
            {hoverSegment ? hoverSegment.count : grandTotal}
          </Typography>
          <Typography sx={{
            fontSize: 8.5, fontWeight: 600,
            color: SLATE,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            mt: 0.6,
            maxWidth: 100,
            mx: "auto",
            lineHeight: 1.2,
          }}>
            {hoverSegment ? hoverSegment.label : "Active Leads"}
          </Typography>
        </Box>
      </Box>

      {/* ── Vertical legend ──────────────────────────────────────── */}
      <Stack gap={0.9} sx={{ flex: 1, minWidth: 0 }}>
        {segments.map((s) => {
          const pct = grandTotal > 0 ? (s.count / grandTotal) * 100 : 0;
          return (
            <Stack key={s.key} direction="row" alignItems="center" gap={1}>
              <Box sx={{
                width: 8, height: 8, borderRadius: "50%",
                bgcolor: s.color, flexShrink: 0,
                boxShadow: s.count > 0 ? `0 0 6px ${s.color}66` : "none",
              }} />
              <Typography sx={{ fontSize: 11.5, color: INK, flex: 1, letterSpacing: "-0.005em" }} noWrap>
                {s.label}
              </Typography>
              <Typography sx={{
                fontFamily: FONT_SERIF,
                fontSize: 13, fontWeight: 400,
                color: s.count > 0 ? INK : SLATE,
                fontVariantNumeric: "tabular-nums",
              }}>
                {s.count}
              </Typography>
              <Box sx={{
                width: 28, height: 3, borderRadius: 999,
                bgcolor: HAIRLINE,
                position: "relative", flexShrink: 0,
                overflow: "hidden",
              }}>
                <Box sx={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: `${pct}%`,
                  bgcolor: s.color,
                  borderRadius: 999,
                  transition: "width 600ms cubic-bezier(0.16, 1, 0.3, 1)",
                }} />
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
}
